import { inject, Injectable } from '@angular/core'
import { PeopleProfile } from '@ftr/annotations-contracts'
import { PagedResultBuilder } from '@ftr/api-shared'
import { PagedResult } from '@ftr/contracts/api/core'
import { Uuid } from '@ftr/contracts/type/shared'
import { RemoteData, tapFailure, unwrapData } from '@ftr/foundation'
import { Action, createSelector, Selector, State, StateContext, StateOperator } from '@ngxs/store'
import { patch } from '@ngxs/store/operators'
import { memoize } from 'lodash-es'
import { firstValueFrom, Observable } from 'rxjs'
import { PeopleProfilesService } from '../../services'
import { FetchPeopleProfileCommand, InitialisePeopleProfilesCommand } from './people-profiles.commands'
import { PeopleProfileInstanceStateModel, PeopleProfileStateModel } from './people-profiles.model'

export const PEOPLE_PROFILE_PAGE_SIZE = 100
export const emptyProfileState = PagedResultBuilder.fromArray<PeopleProfile>([])
emptyProfileState.meta.size = PEOPLE_PROFILE_PAGE_SIZE
export const getDefaultPeopleProfileState: () => PeopleProfileStateModel = () => ({
  instances: {},
})

@State({
  name: 'peopleProfiles',
  defaults: getDefaultPeopleProfileState(),
})
@Injectable()
export class PeopleProfilesState {
  private readonly peopleProfilesService = inject(PeopleProfilesService)

  @Selector()
  static allInstanceStates(state: PeopleProfileStateModel): Record<Uuid, PeopleProfileInstanceStateModel> {
    return state?.instances ?? {}
  }

  static readonly peopleProfiles = memoize(
    (
      courtSystemId: string,
    ): ((sourceStates: ReturnType<typeof PeopleProfilesState.allInstanceStates>) => PagedResult<PeopleProfile>) =>
      createSelector([PeopleProfilesState.allInstanceStates], sourceStates => {
        const { peopleProfiles = emptyProfileState } = sourceStates[courtSystemId] ?? {}

        return peopleProfiles
      }),
  )

  static readonly status = memoize(
    (
      courtSystemId: string,
    ): ((sourceStates: ReturnType<typeof PeopleProfilesState.allInstanceStates>) => RemoteData<{ hasMore: boolean }>) =>
      createSelector([PeopleProfilesState.allInstanceStates], sourceStates => {
        return sourceStates[courtSystemId]?.status ?? RemoteData.notAsked()
      }),
    courtSystemId => courtSystemId,
  )

  @Action(InitialisePeopleProfilesCommand)
  private initialisePeopleProfileCommand(
    { setState, getState, dispatch }: StateContext<PeopleProfileStateModel>,
    { courtSystemId }: InitialisePeopleProfilesCommand,
  ): Observable<void> | undefined {
    const { status } = getState().instances[courtSystemId] ?? {}
    if (status?.isCompleted() || status?.isLoading()) {
      return
    }

    setState(
      makeInstancePatch(courtSystemId, {
        peopleProfiles: emptyProfileState,
        status: RemoteData.notAsked(),
      }),
    )

    return dispatch(new FetchPeopleProfileCommand(courtSystemId))
  }

  @Action(FetchPeopleProfileCommand)
  private async fetchPeopleProfiles(
    { getState, setState }: StateContext<PeopleProfileStateModel>,
    { courtSystemId }: FetchPeopleProfileCommand,
  ): Promise<void> {
    const { status = RemoteData.notAsked() } = getState().instances[courtSystemId] ?? {}

    if (status.isLoading()) {
      return
    }

    setState(
      makeInstancePatch(courtSystemId, {
        status: RemoteData.loading(),
      }),
    )

    // add options to fetch next page when we need to
    const offset = 0
    const limit = PEOPLE_PROFILE_PAGE_SIZE

    try {
      const result = await firstValueFrom(
        this.peopleProfilesService.getPeopleProfiles({ courtSystemId, offset, limit }).pipe(
          tapFailure(err => {
            throw err
          }),
          unwrapData(),
        ),
      )

      setState(
        makeInstancePatch(courtSystemId, {
          peopleProfiles: {
            items: result.items,
            meta: {
              totalItems: result.meta.totalItems,
              number: result.meta.pageNumber,
              size: PEOPLE_PROFILE_PAGE_SIZE,
            },
          },
          status: RemoteData.success({ hasMore: result.items.length === limit }),
        }),
      )
    } catch (err) {
      setState(
        makeInstancePatch(courtSystemId, {
          peopleProfiles: emptyProfileState,
          status: RemoteData.failure(new Error('Failed to fetch people profiles', { cause: err })),
        }),
      )
    }
  }
}

function makeInstancePatch(
  instanceId: string,
  instancePartial: Partial<PeopleProfileInstanceStateModel>,
): StateOperator<PeopleProfileStateModel> {
  return patch({
    instances: patch({
      [instanceId]: patch(instancePartial),
    }),
  })
}
